import { get, getRaw, post } from '../api.js';
import { deriveAndStoreKeys, getAuthKeyHex, decryptItem, decryptFileBytes, clearKeys, hasKeys, deriveRecoveryKeys, unwrapMasterKeyFromRecovery, validateBip39Mnemonic, generateBip39Mnemonic, wipe } from '../crypto.js';
import { setTopbar, lucideIcons, esc } from '../app.js';
import { navigate } from '../router.js';
import { clearSession, isLimited } from '../auth-store.js';
import { crc32, deflateRaw, dosDateTime, AE_EXTRA_LEN, AE_SALT_LEN, AE_PVV_LEN, AE_AUTH_LEN, AE_OVERHEAD, writeAeExtra, aesEncryptEntry, AesZipStreamWriter } from '../zip.js';
import { createStreamDownload } from '../stream-download.js';

export async function renderCloseAccount(container) {
  setTopbar('Close Account', '');

  let me;
  try {
    me = await get('/auth/me');
  } catch (err) {
    container.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
    return;
  }

  // Limited (magic-link-only) sessions take a separate flow: no password
  // verification, no backup export (no vault key to decrypt items with), and
  // the deletion is queued with a grace period instead of running immediately.
  if (isLimited()) {
    return renderLimitedClose(container, me);
  }

  // Track which verification method was used for the API call
  let recoveryAuthHex = null;

  container.innerHTML = `
    <div style="max-width:540px">
      <div id="close-step-1">
        <div class="msg msg-error" style="margin-bottom:20px">
          <strong>This is irreversible.</strong> All items, grants, wills, passkeys, groups, notarizations, and audit logs will be permanently deleted. There is no way to recover your data after closing your account.
        </div>

        <div class="card" style="display:flex;flex-direction:column;gap:12px">
          <div id="verify-password-section">
            <div class="field">
              <label>Enter your passphrase to continue</label>
              <input type="password" id="close-password" required placeholder="Your vault passphrase">
            </div>
            <div id="close-step1-msg"></div>
            <button class="btn btn-primary" id="close-verify-btn" style="margin-top:8px">Verify &amp; Continue</button>
          </div>

          ${me.has_recovery ? `
          <div style="border-top:1px solid var(--border);padding-top:12px;margin-top:4px">
            <button class="btn btn-secondary btn-sm" id="toggle-recovery-btn">Use recovery words instead</button>
          </div>

          <div id="verify-recovery-section" style="display:none">
            <div class="field">
              <label>Enter your 12 recovery words</label>
            </div>
            <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:8px">
              ${Array.from({ length: 12 }, (_, i) =>
                `<div class="field" style="margin-bottom:0"><label style="font-size:11px">Word ${i + 1}</label><input type="text" name="rw${i}" class="recovery-word-input" autocomplete="off" autocapitalize="none" style="font-family:monospace;font-size:13px"></div>`
              ).join('')}
            </div>
            <div id="close-recovery-msg" style="margin-top:8px"></div>
            <button class="btn btn-primary" id="close-verify-recovery-btn" style="margin-top:8px">Verify &amp; Continue</button>
            <div style="border-top:1px solid var(--border);padding-top:12px;margin-top:12px">
              <button class="btn btn-secondary btn-sm" id="toggle-password-btn">Use passphrase instead</button>
            </div>
          </div>
          ` : ''}
        </div>
      </div>

      <div id="close-step-2" style="display:none">
        <div class="section-title">Optional: Export Backup</div>
        <p style="color:var(--text-secondary);font-size:13px;margin-bottom:16px">
          Download an AES-256 encrypted ZIP of all your vault items. We'll generate a 12-word
          backup password — save it now, then use it to open the ZIP with 7-Zip, WinRAR, or WinZip.
        </p>

        <div class="card" style="display:flex;flex-direction:column;gap:12px">
          <div>
            <label style="display:block;margin-bottom:6px">Backup password</label>
            <div id="backup-password-display"></div>
            <p style="font-size:12px;color:var(--text-secondary);margin-top:8px">
              The ZIP's archive format (WinZip AE-2) uses a weak key-derivation function, so a
              user-chosen password could be cracked offline. A 12-word random phrase has enough
              entropy to remain secure regardless. There is no way to recover this password —
              if you lose it, the backup is unreadable.
            </p>
          </div>
          <label style="display:flex;align-items:center;gap:8px;font-size:14px;color:var(--text-secondary);cursor:pointer">
            <input type="checkbox" id="backup-saved-check"> I have saved this backup password
          </label>
          <div id="export-msg"></div>
          <div id="export-progress" style="display:none">
            <div style="background:var(--bg-card);border-radius:4px;height:6px;overflow:hidden;margin-bottom:8px">
              <div id="export-bar" style="height:100%;background:var(--accent);width:0%;transition:width 0.3s"></div>
            </div>
            <div id="export-status" style="font-size:12px;color:var(--text-secondary)"></div>
          </div>
          <div style="display:flex;gap:8px">
            <button class="btn btn-primary btn-sm" id="export-btn" disabled>Export Backup</button>
            <button class="btn btn-secondary btn-sm" id="skip-export-btn">Skip &amp; Delete</button>
          </div>
        </div>
      </div>

      <div id="close-step-3" style="display:none">
        <div class="section-title">Final Confirmation</div>
        <div class="msg msg-error" style="margin-bottom:16px">
          This will permanently delete your account and all data. This cannot be undone.
        </div>
        <div class="card" style="display:flex;flex-direction:column;gap:12px">
          <div class="field">
            <label>Type <strong>DELETE</strong> to confirm</label>
            <input type="text" id="close-confirm-text" placeholder="DELETE" autocomplete="off">
          </div>
          <div id="close-step3-msg"></div>
          <button class="btn btn-danger" id="close-final-btn" disabled>Close Account Permanently</button>
        </div>
      </div>
    </div>
  `;

  lucideIcons();

  const step1 = document.getElementById('close-step-1');
  const step2 = document.getElementById('close-step-2');
  const step3 = document.getElementById('close-step-3');
  const step1Msg = document.getElementById('close-step1-msg');
  const verifyBtn = document.getElementById('close-verify-btn');
  const passwordInput = document.getElementById('close-password');

  // Toggle between password and recovery word sections
  const toggleRecoveryBtn = document.getElementById('toggle-recovery-btn');
  const togglePasswordBtn = document.getElementById('toggle-password-btn');
  const passwordSection = document.getElementById('verify-password-section');
  const recoverySection = document.getElementById('verify-recovery-section');

  if (toggleRecoveryBtn) {
    toggleRecoveryBtn.onclick = () => {
      passwordSection.style.display = 'none';
      recoverySection.style.display = '';
      toggleRecoveryBtn.parentElement.style.display = 'none';
    };
  }
  if (togglePasswordBtn) {
    togglePasswordBtn.onclick = () => {
      recoverySection.style.display = 'none';
      passwordSection.style.display = '';
      if (toggleRecoveryBtn) toggleRecoveryBtn.parentElement.style.display = '';
    };
  }

  // Step 1a: Verify via password
  verifyBtn.onclick = async () => {
    const password = passwordInput.value;
    if (!password) {
      step1Msg.innerHTML = '<div class="msg msg-error">Enter your passphrase.</div>';
      return;
    }

    verifyBtn.disabled = true;
    verifyBtn.textContent = 'Verifying...';
    step1Msg.innerHTML = '';

    try {
      deriveAndStoreKeys(
        password,
        me.client_salt,
        me.encrypted_private_key,
        me.public_key,
        me.encrypted_master_key,
        me.encrypted_master_key_format_version,
        me.encrypted_private_key_format_version,
      );
    } catch (err) {
      step1Msg.innerHTML = `<div class="msg msg-error">Incorrect passphrase: ${esc(err.message)}</div>`;
      verifyBtn.disabled = false;
      verifyBtn.textContent = 'Verify & Continue';
      return;
    }

    step1.style.display = 'none';
    step2.style.display = '';
    await enterStep2();
  };

  passwordInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') verifyBtn.click();
  });

  // Step 1b: Verify via recovery words
  const recoveryVerifyBtn = document.getElementById('close-verify-recovery-btn');
  const recoveryMsg = document.getElementById('close-recovery-msg');

  if (recoveryVerifyBtn) {
    recoveryVerifyBtn.onclick = async () => {
      const words = [];
      for (let i = 0; i < 12; i++) {
        const input = container.querySelector(`[name="rw${i}"]`);
        words.push(input.value.trim().toLowerCase());
      }
      const mnemonic = words.join(' ');

      if (words.some(w => !w)) {
        recoveryMsg.innerHTML = '<div class="msg msg-error">Please enter all 12 words.</div>';
        return;
      }

      const valid = await validateBip39Mnemonic(mnemonic);
      if (!valid) {
        recoveryMsg.innerHTML = '<div class="msg msg-error">Invalid recovery words. Check spelling and order.</div>';
        return;
      }

      recoveryVerifyBtn.disabled = true;
      recoveryVerifyBtn.textContent = 'Verifying...';
      recoveryMsg.innerHTML = '';

      let recovery;
      try {
        recovery = await deriveRecoveryKeys(mnemonic, me.recovery_version || 1);
        recoveryAuthHex = Array.from(recovery.authKey).map(b => b.toString(16).padStart(2, '0')).join('');

        // Unwrap master key — this stores keys in memory so export works
        unwrapMasterKeyFromRecovery(
          recovery.wrappingKey,
          me.recovery_wrapped_master_key,
          me.encrypted_private_key,
          me.public_key,
          me.recovery_wrapped_master_key_format_version,
          me.encrypted_private_key_format_version,
        );
      } catch (err) {
        recoveryMsg.innerHTML = '<div class="msg msg-error">Wrong recovery words or decryption failed.</div>';
        recoveryVerifyBtn.disabled = false;
        recoveryVerifyBtn.textContent = 'Verify & Continue';
        return;
      } finally {
        if (recovery) wipe(recovery.wrappingKey, recovery.authKey);
      }

      step1.style.display = 'none';
      step2.style.display = '';
      await enterStep2();
    };
  }

  // Step 2: Optional export
  const exportBtn = document.getElementById('export-btn');
  const skipBtn = document.getElementById('skip-export-btn');
  const exportMsg = document.getElementById('export-msg');
  let backupMnemonic = null;

  async function enterStep2() {
    try {
      backupMnemonic = await generateBip39Mnemonic();
    } catch (err) {
      exportMsg.innerHTML = `<div class="msg msg-error">Failed to generate backup password: ${esc(err.message)}. You can still skip the export and proceed.</div>`;
      return;
    }

    const words = backupMnemonic.split(' ');
    const grid = words
      .map((w, i) => `<div class="recovery-word"><span class="recovery-num">${i + 1}.</span> ${esc(w)}</div>`)
      .join('');
    document.getElementById('backup-password-display').innerHTML = `
      <div class="recovery-grid">${grid}</div>
      <div style="display:flex;gap:8px;margin-top:8px">
        <button type="button" class="btn btn-secondary btn-sm" id="copy-backup-pw">
          <i data-lucide="copy" style="width:14px;height:14px"></i> Copy
        </button>
      </div>
    `;
    lucideIcons();

    document.getElementById('copy-backup-pw').onclick = () => {
      navigator.clipboard.writeText(backupMnemonic).then(() => {
        const btn = document.getElementById('copy-backup-pw');
        btn.textContent = 'Copied!';
        setTimeout(() => { navigator.clipboard.writeText(''); }, 60000);
        setTimeout(() => {
          btn.innerHTML = '<i data-lucide="copy" style="width:14px;height:14px"></i> Copy';
          lucideIcons();
        }, 2000);
      });
    };

    document.getElementById('backup-saved-check').onchange = (e) => {
      exportBtn.disabled = !e.target.checked;
    };
  }

  exportBtn.onclick = async () => {
    if (!backupMnemonic) {
      exportMsg.innerHTML = '<div class="msg msg-error">Backup password not generated yet.</div>';
      return;
    }

    exportBtn.disabled = true;
    skipBtn.disabled = true;
    exportMsg.innerHTML = '';
    document.getElementById('export-progress').style.display = '';

    try {
      await exportBackup(backupMnemonic);
      backupMnemonic = null;
      step2.style.display = 'none';
      step3.style.display = '';
    } catch (err) {
      exportMsg.innerHTML = `<div class="msg msg-error">Export failed: ${esc(err.message)}</div>`;
      exportBtn.disabled = false;
      skipBtn.disabled = false;
    }
  };

  skipBtn.onclick = () => {
    step2.style.display = 'none';
    step3.style.display = '';
  };

  // Step 3: Final confirmation
  const confirmInput = document.getElementById('close-confirm-text');
  const finalBtn = document.getElementById('close-final-btn');
  const step3Msg = document.getElementById('close-step3-msg');

  confirmInput.oninput = () => {
    finalBtn.disabled = confirmInput.value.trim() !== 'DELETE';
  };

  finalBtn.onclick = async () => {
    if (confirmInput.value.trim() !== 'DELETE') return;

    finalBtn.disabled = true;
    finalBtn.textContent = 'Closing account...';
    step3Msg.innerHTML = '';

    try {
      const body = recoveryAuthHex
        ? { recovery_auth_key: recoveryAuthHex }
        : { auth_key: getAuthKeyHex() };
      await post('/auth/close-account', body);

      clearKeys();
      clearSession();

      navigate('#/drop');
    } catch (err) {
      step3Msg.innerHTML = `<div class="msg msg-error">${esc(err.message)}</div>`;
      finalBtn.disabled = false;
      finalBtn.textContent = 'Close Account Permanently';
    }
  };
}

// ---------------------------------------------------------------------------
//  Limited-session flow: schedule deletion with a 7-day grace period.
// ---------------------------------------------------------------------------

function renderLimitedClose(container, me) {
  const pending = me.pending_account_deletion;

  if (pending) {
    const when = new Date(pending.scheduled_for).toLocaleString();
    container.innerHTML = `
      <div style="max-width:540px">
        <div class="msg msg-warning" style="margin-bottom:20px">
          <strong>Deletion already scheduled for ${esc(when)}.</strong>
          Check your email for a cancellation link, or sign in with your password to cancel automatically.
        </div>
        <div class="card" style="display:flex;flex-direction:column;gap:12px">
          <p style="color:var(--text-secondary);font-size:13px;margin:0">
            To cancel from here, click the link in the confirmation email we sent, or sign in with your password — any full-access sign-in cancels the pending deletion.
          </p>
          <div style="display:flex;gap:8px;flex-wrap:wrap">
            <a href="#/unlock" class="btn btn-primary btn-sm" style="text-decoration:none">Sign in with password to cancel</a>
            <a href="#/account" class="btn btn-secondary btn-sm" style="text-decoration:none">Back to account</a>
          </div>
        </div>
      </div>`;
    lucideIcons();
    return;
  }

  container.innerHTML = `
    <div style="max-width:540px">
      <div class="msg msg-warning" style="margin-bottom:20px">
        <strong>7-day grace period.</strong>
        Because you're signed in with a magic link alone, we queue the deletion instead of running it immediately. If this was someone else, they'll have a week to notice and cancel.
      </div>

      <div class="section-title">What happens next</div>
      <div class="card" style="margin-bottom:20px">
        <ul style="margin:0;padding-left:20px;color:var(--text-secondary);font-size:14px;line-height:1.6">
          <li>We'll email you a confirmation with a cancellation link.</li>
          <li>After 7 days, your account and all vault data will be permanently deleted.</li>
          <li>Any sign-in with your password during the grace period cancels the deletion and sends a notice.</li>
        </ul>
      </div>

      <div class="msg msg-error" style="margin-bottom:16px">
        <strong>If you still remember your password</strong>, consider signing in first — you'll be able to export an encrypted backup before deletion, and you can delete immediately instead of waiting 7 days. <a href="#/unlock" style="color:var(--accent)">Sign in now</a>.
      </div>

      <div class="section-title">Confirm</div>
      <div class="card" style="display:flex;flex-direction:column;gap:12px">
        <div class="field">
          <label>Type <strong>DELETE</strong> to confirm</label>
          <input type="text" id="limited-confirm-text" placeholder="DELETE" autocomplete="off">
        </div>
        <div id="limited-close-msg"></div>
        <div style="display:flex;gap:8px;flex-wrap:wrap">
          <button class="btn btn-danger" id="limited-close-btn" disabled>Schedule deletion (7 days)</button>
          <a href="#/account" class="btn btn-secondary" style="text-decoration:none">Cancel</a>
        </div>
      </div>
    </div>`;

  lucideIcons();

  const confirmInput = document.getElementById('limited-confirm-text');
  const finalBtn = document.getElementById('limited-close-btn');
  const msg = document.getElementById('limited-close-msg');

  confirmInput.oninput = () => {
    finalBtn.disabled = confirmInput.value.trim() !== 'DELETE';
  };

  finalBtn.onclick = async () => {
    if (confirmInput.value.trim() !== 'DELETE') return;
    finalBtn.disabled = true;
    finalBtn.textContent = 'Scheduling...';
    msg.innerHTML = '';

    try {
      const result = await post('/auth/close-account', {});
      const when = result.scheduled_for
        ? new Date(result.scheduled_for).toLocaleString()
        : 'unknown';
      container.innerHTML = `
        <div style="max-width:540px">
          <div class="msg msg-success" style="margin-bottom:16px">
            <strong>Deletion scheduled for ${esc(when)}.</strong>
          </div>
          <div class="card">
            <p style="color:var(--text-secondary);font-size:14px;line-height:1.6">
              We've sent a confirmation email with a cancellation link. If you change your mind, click that link — or sign in with your password any time during the grace period to cancel automatically.
            </p>
          </div>
          <div style="margin-top:16px">
            <a href="#/account" class="btn btn-secondary" style="text-decoration:none">Back to account</a>
          </div>
        </div>`;
      lucideIcons();
    } catch (err) {
      msg.innerHTML = `<div class="msg msg-error">${esc(err.message || String(err))}</div>`;
      finalBtn.disabled = false;
      finalBtn.textContent = 'Schedule deletion (7 days)';
    }
  };
}

// ---------------------------------------------------------------------------
//  Export: decrypt all vault items → AES-256 encrypted ZIP (WinZip AE-2)
// ---------------------------------------------------------------------------

function sanitizeFilename(name) {
  return name.replace(/[/\\:*?"<>|]/g, '_').slice(0, 200) || 'unnamed';
}

function triggerDownload(data, filename) {
  const blob = new Blob([data], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function decryptItemToEntries(item, i) {
  const entries = [];
  try {
    const plaintext = decryptItem(item.encrypted_blob, item.wrapped_key, item.nonce);
    const meta = JSON.parse(plaintext);
    const safeName = sanitizeFilename(meta.name || meta.title || `item-${i}`);
    const dir = `items/${item.id}/`;

    entries.push([dir + 'metadata.json', new TextEncoder().encode(JSON.stringify(meta, null, 2))]);

    if (item.file_blob_key) {
      try {
        const blobData = await getRaw(`/items/${item.id}/blob`);
        const fileWK = meta.file_wrapped_key || item.wrapped_key;
        const fileNonce = meta.file_nonce || item.nonce;
        const decrypted = decryptFileBytes(new Uint8Array(blobData), fileWK, fileNonce);
        entries.push([dir + safeName, decrypted]);
      } catch (blobErr) {
        entries.push([dir + 'blob-error.txt', new TextEncoder().encode(`Failed to fetch blob: ${blobErr.message}`)]);
      }
    }
  } catch (decErr) {
    entries.push([`items/${item.id}/decrypt-error.txt`, new TextEncoder().encode(`Failed to decrypt: ${decErr.message}`)]);
  }
  return entries;
}

async function exportBackup(zipPassword) {
  const bar = document.getElementById('export-bar');
  const status = document.getElementById('export-status');
  const setProgress = (pct, msg) => {
    bar.style.width = pct + '%';
    status.textContent = msg;
  };

  setProgress(5, 'Fetching items...');
  const items = await get('/items');

  if (!items.length) {
    setProgress(100, 'No items to export.');
    triggerDownload(new Uint8Array(0), 'blindkeep-backup-empty.txt');
    return;
  }

  const date = new Date().toISOString().slice(0, 10);
  const filename = `blindkeep-backup-${date}.zip`;

  // Try streaming path
  const stream = await createStreamDownload(filename);
  if (stream) {
    try {
      const zipWriter = new AesZipStreamWriter((chunk) => stream.writer.write(chunk), zipPassword);

      for (let i = 0; i < items.length; i++) {
        setProgress(10 + Math.floor((i / items.length) * 80), `Encrypting item ${i + 1}/${items.length}...`);
        const entries = await decryptItemToEntries(items[i], i);
        for (const [name, data] of entries) {
          await zipWriter.addEntry(name, data);
        }
      }

      setProgress(95, 'Finalizing...');
      await zipWriter.finish();
      stream.writer.close();
      stream.cleanup();
      setProgress(100, 'Download ready.');
      return;
    } catch (err) {
      try { stream.writer.abort(err.message); } catch (_) {}
      stream.cleanup();
      throw err;
    }
  }

  // Fallback: in-memory build
  const files = {};
  const total = items.length;

  for (let i = 0; i < total; i++) {
    const item = items[i];
    setProgress(10 + Math.floor((i / total) * 60), `Decrypting item ${i + 1}/${total}...`);
    const entries = await decryptItemToEntries(item, i);
    for (const [name, data] of entries) {
      files[name] = data;
    }
  }

  setProgress(75, 'Building encrypted ZIP...');

  const zipData = await buildAesZip(files, zipPassword, (filePct) => {
    setProgress(75 + Math.floor(filePct * 0.20), 'Encrypting ZIP entries...');
  });

  setProgress(98, 'Downloading...');
  triggerDownload(zipData, filename);
  setProgress(100, 'Download ready.');
}

// ---------------------------------------------------------------------------
//  In-memory AES ZIP builder (fallback)
// ---------------------------------------------------------------------------

async function buildAesZip(files, password, onProgress) {
  const entries = Object.entries(files);
  const { time: dosTime, date: dosDate } = dosDateTime();

  const encrypted = [];
  for (let i = 0; i < entries.length; i++) {
    encrypted.push(await aesEncryptEntry(password, entries[i][1]));
    if (onProgress) onProgress((i + 1) / entries.length * 100);
  }

  const localParts = [];
  const centralParts = [];
  let offset = 0;

  for (let i = 0; i < entries.length; i++) {
    const [name, plainData] = entries[i];
    const encData = encrypted[i];
    const nameBytes = new TextEncoder().encode(name);
    const crc = crc32(plainData);

    const compressedSize = encData.length;
    const uncompressedSize = plainData.length;

    const localHdr = new Uint8Array(30 + nameBytes.length + AE_EXTRA_LEN);
    const lv = new DataView(localHdr.buffer);
    lv.setUint32(0,  0x04034b50, true);
    lv.setUint16(4,  0x0033, true);
    lv.setUint16(6,  0x0001, true);
    lv.setUint16(8,  99, true);
    lv.setUint16(10, dosTime, true);
    lv.setUint16(12, dosDate, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, compressedSize, true);
    lv.setUint32(22, uncompressedSize, true);
    lv.setUint16(26, nameBytes.length, true);
    lv.setUint16(28, AE_EXTRA_LEN, true);
    localHdr.set(nameBytes, 30);
    writeAeExtra(localHdr, 30 + nameBytes.length);
    localParts.push(localHdr);
    localParts.push(encData);

    const centHdr = new Uint8Array(46 + nameBytes.length + AE_EXTRA_LEN);
    const cv = new DataView(centHdr.buffer);
    cv.setUint32(0,  0x02014b50, true);
    cv.setUint16(4,  0x0033, true);
    cv.setUint16(6,  0x0033, true);
    cv.setUint16(8,  0x0001, true);
    cv.setUint16(10, 99, true);
    cv.setUint16(12, dosTime, true);
    cv.setUint16(14, dosDate, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, compressedSize, true);
    cv.setUint32(24, uncompressedSize, true);
    cv.setUint16(28, nameBytes.length, true);
    cv.setUint16(30, AE_EXTRA_LEN, true);
    cv.setUint16(32, 0, true);
    cv.setUint16(34, 0, true);
    cv.setUint16(36, 0, true);
    cv.setUint32(38, 0, true);
    cv.setUint32(42, offset, true);
    centHdr.set(nameBytes, 46);
    writeAeExtra(centHdr, 46 + nameBytes.length);
    centralParts.push(centHdr);

    offset += localHdr.length + encData.length;
  }

  const cdSize = centralParts.reduce((s, c) => s + c.length, 0);

  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0,  0x06054b50, true);
  ev.setUint16(4,  0, true);
  ev.setUint16(6,  0, true);
  ev.setUint16(8,  entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, offset, true);
  ev.setUint16(20, 0, true);

  const totalSize = offset + cdSize + 22;
  const out = new Uint8Array(totalSize);
  let pos = 0;
  for (const p of localParts) { out.set(p, pos); pos += p.length; }
  for (const p of centralParts) { out.set(p, pos); pos += p.length; }
  out.set(eocd, pos);
  return out;
}
